import { memo, useContext, useCallback, useMemo, useRef } from 'react';

import {
    Avatar,
    Text,
    Icon,
    IconMultiple,
    useTheme,
    Popover,
    Spacer,
    Markdown,
} from '@Sage/hoi-poi-ui';
import { isEmptyObject } from '@Sage/ssm-utils';
import classnames from 'classnames';

import DeleteModal from 'components/DeleteModal';
import { getLiteral } from 'utils/getLiteral';

import { findNodeByType } from '../../../../utils';
import { AutomationBuilderCanvasContext } from '../../AutomationBuilderCanvasContext';
import AutomationBuilderAddNodesButton from '../AutomationBuilderAddNodesButton';
import './styles.scss';

const AutomationBuilderBox = memo(
    ({
        id,
        className,
        title,
        icon,
        secondIcon,
        emptyPlaceholder,
        textValue,
        children,
        isEnd,
        allowDelete,
        isEveryTime,
        isIncomplete,
    }) => {
        const theme = useTheme();
        const { state, dispatch, data, onDeleteNode } = useContext(AutomationBuilderCanvasContext);
        const deleteModalRef = useRef();
        const onSelect = useCallback(
            (e) => {
                e.stopPropagation();

                requestAnimationFrame(() => {
                    const automationRoot = document.querySelector('.fm-automation-builder__canvas');
                    if (automationRoot) {
                        const mouseDownEvent = new MouseEvent('mousedown', {
                            bubbles: true,
                            cancelable: true,
                            view: window,
                            clientX: 1,
                            clientY: 1,
                        });
                        automationRoot.dispatchEvent(mouseDownEvent);
                    }
                });

                dispatch({
                    type: state.selected ? 'nextSelect' : 'select',
                    node: findNodeByType(data?.scene, id),
                });
            },
            [data?.scene, dispatch, id, state.selected],
        );

        const isDisabled = useMemo(
            () => isEmptyObject(data.scene?.parameters || {}) && id !== data.scene?.id,
            [data?.scene, id],
        );

        const showPlaceholder =
            !textValue && emptyPlaceholder && (!children || children.length === 0);
        const avatarIcon = useMemo(() => {
            if (!secondIcon) return <Avatar icon={icon} size="big" />;
            return <IconMultiple firstIcon={icon} secondIcon={secondIcon} size="large" />;
        }, [icon, secondIcon]);

        const executionModeConfig = useMemo(() => {
            const dynamicConfigs = {
                everyTime: {
                    label: getLiteral('label_automations_execution_mode_run_every_time'),
                    desc: getLiteral('label_automations_execution_mode_run_every_time_desc'),
                    icon: 'automationsRepeat',
                },
                once: {
                    label: getLiteral('label_automations_execution_mode_run_once'),
                    desc: getLiteral('label_automations_execution_mode_run_once_desc'),
                    icon: 'automationsOnce',
                },
            };

            return isEveryTime ? dynamicConfigs.everyTime : dynamicConfigs.once;
        }, [isEveryTime]);

        const onDeleteRef = useCallback((ref) => {
            deleteModalRef.current = ref;
        }, []);

        const openDeleteModal = useCallback((e) => {
            e.stopPropagation();
            deleteModalRef.current.open();
        }, []);

        const onDeleteConfirm = useCallback(() => {
            onDeleteNode(id);
            return Promise.resolve();
        }, [id, onDeleteNode]);

        const popoverEveryTimeProps = useMemo(() => {
            return {
                trigger: ['hover'],
                content: (
                    <div>
                        <Text type="subtitle" medium>
                            {getLiteral('label_automations_execution_mode')}
                        </Text>
                        <Spacer y={2} />
                        <div className="fm-automation-builder__box__every-time__title">
                            <Icon name={executionModeConfig.icon} />
                            <Text>{executionModeConfig.label}</Text>
                        </div>
                        <Spacer y={1} />
                        <Markdown
                            content={executionModeConfig.desc}
                            overrides={{
                                a: { target: '_blank' },
                                p: {
                                    type: 'caption',
                                    color: theme.colors.textLight.secondary,
                                },
                            }}
                        />
                    </div>
                ),
            };
        }, [executionModeConfig, theme.colors.textLight.secondary]);

        return (
            <div className="fm-automation-builder__box-container">
                <div
                    className={classnames({
                        'fm-automation-builder__box--disabled': isDisabled,
                        'fm-automation-builder__box--incomplete': isIncomplete,
                    })}
                >
                    <div
                        className={classnames('fm-automation-builder__box', className, {
                            'fm-automation-builder__box--selected': state.selected?.id === id,
                        })}
                        onClick={isDisabled || state.selected?.id === id ? undefined : onSelect}
                    >
                        <div className="fm-automation-builder__box__header">
                            <div className="fm-automation-builder__box__header-title">
                                {avatarIcon}
                                <Text medium>{title}</Text>
                            </div>
                            <div className="fm-automation-builder__box__header-icons">
                                {allowDelete && (
                                    <span className="fm-automation-builder__box__delete">
                                        <Icon
                                            tooltip={getLiteral('action_delete')}
                                            onClick={openDeleteModal}
                                            name="delete"
                                        />
                                    </span>
                                )}
                                {isEveryTime !== undefined && (
                                    <Popover {...popoverEveryTimeProps}>
                                        <span>
                                            <Icon
                                                className="fm-automation-builder__box__run"
                                                name={
                                                    isEveryTime
                                                        ? 'automationsRepeat'
                                                        : 'automationsOnce'
                                                }
                                            />
                                        </span>
                                    </Popover>
                                )}
                            </div>
                        </div>
                        <div className="fm-automation-builder__box__content">
                            {textValue && (
                                <Text type="subtitle" medium>
                                    {textValue}
                                </Text>
                            )}
                            {showPlaceholder && (
                                <Text type="subtitle" medium>
                                    {emptyPlaceholder}
                                </Text>
                            )}
                            {children}
                        </div>
                    </div>
                </div>
                {isEnd && (
                    <div className="fm-automation-builder__box__add-nodes">
                        <div className="fm-automation-builder__box__add-nodes-line" />
                        <AutomationBuilderAddNodesButton parentId={id} />
                    </div>
                )}
                <DeleteModal
                    overlayClassName="fm-automation-builder__modal"
                    title={getLiteral('title_delete')}
                    body={getLiteral('label_title_delete_registry_modal')}
                    confirmText={getLiteral('action_delete')}
                    onRef={onDeleteRef}
                    onDelete={onDeleteConfirm}
                />
            </div>
        );
    },
);

export default AutomationBuilderBox;
